import { Database } from "lucide-react";
import { DiscordChannel, GuildConfig } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Input } from "@/components/ui/input";

type Props = {
  config: GuildConfig;
  channels: DiscordChannel[];
  onChange: (v: GuildConfig["sql_backup"]) => void;
};

export function SqlBackupTab({ config, channels, onChange }: Props) {
  const cfg = config.sql_backup;
  const update = <K extends keyof typeof cfg>(k: K, v: (typeof cfg)[K]) =>
    onChange({ ...cfg, [k]: v });
  void channels;

  return (
    <Section
      icon={Database}
      title="SQL backup"
      description="Automatic MySQL/MariaDB backups delivered to a Discord webhook."
      enabled={cfg.enabled}
      onToggle={(v) => update("enabled", v)}
    >
      <div className="rounded-lg border border-dashed border-border bg-muted/25 p-4 text-sm text-muted-foreground space-y-2">
        <p className="font-medium text-foreground">How it works</p>
        <p>
          <strong>No Discord commands.</strong> The bot process connects to MySQL/MariaDB on the host, dumps on the
          interval you set, and uploads the archive to the webhook URL. Keep credentials secret.
        </p>
      </div>

      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-muted-foreground">
        Enter your database connection details below. The bot will create backups on the selected schedule
        and send the backup ZIP to the configured webhook.
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Host"><Input value={cfg.host} onChange={(e) => update("host", e.target.value)} /></Field>
        <Field label="Port"><Input type="number" value={cfg.port} onChange={(e) => update("port", Number(e.target.value) || 3306)} /></Field>
        <Field label="Database"><Input value={cfg.database} onChange={(e) => update("database", e.target.value)} /></Field>
        <Field label="Username"><Input value={cfg.username} onChange={(e) => update("username", e.target.value)} /></Field>
        <Field label="Database password">
          <Input
            type="password"
            value={cfg.password}
            onChange={(e) => update("password", e.target.value)}
            autoComplete="new-password"
          />
        </Field>
        <Field label="Backup webhook URL" hint="Required. Bot sends the zip file here after backup.">
          <Input
            value={cfg.webhook_url}
            onChange={(e) => update("webhook_url", e.target.value)}
            placeholder="https://discord.com/api/webhooks/..."
          />
        </Field>
        <Field label="Every X hours">
          <Input type="number" min={1} max={168} value={cfg.interval_hours} onChange={(e) => update("interval_hours", Math.max(1, Number(e.target.value) || 24))} />
        </Field>
        <Field label="Keep last N backups">
          <Input type="number" min={1} max={30} value={cfg.retention} onChange={(e) => update("retention", Math.max(1, Number(e.target.value) || 7))} />
        </Field>
      </div>
    </Section>
  );
}
